import { useEffect, useState } from "react";
import { FaPlus, FaMinus, FaSpinner } from "react-icons/fa";
import { toastSuccess, toastError, toastWarning } from "../../services/toaster";

import useAuthStore from "../../services/authStore";

import "./PTCash.css";

const API_URL = process.env.REACT_APP_API_URL;

const CashModulePermission = () => {
  const companyId = useAuthStore((state) => state.companyId);
  const token = useAuthStore((state) => state.token);

  // Existing users API from AuthStore
  const getUsers = useAuthStore((state) => state.getUsers);
  const users = useAuthStore((state) => state.users);
  const usersLoading = useAuthStore((state) => state.usersLoading);

  const [permissionRows, setPermissionRows] = useState([]);
  const [openEmployeeDropdown, setOpenEmployeeDropdown] = useState(null);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [loadingPermissions, setLoadingPermissions] = useState(true);
  const [saving, setSaving] = useState(false);

  /*
   * ============================================================
   * LOAD USERS + CASH PERMISSIONS
   * ============================================================
   */

  useEffect(() => {
    const loadData = async () => {
      if (!companyId) return;

      try {
        setLoadingPermissions(true);

        // API 1:
        // Existing AuthStore users API
        const usersPromise = getUsers();

        // API 2:
        // Cash module permission API
        const permissionPromise = fetch(
          `${API_URL}/cash-module-permission/${companyId}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              ...(token && {
                Authorization: `Bearer ${token}`,
              }),
            },
          },
        );

        const [usersResponse, permissionResponse] = await Promise.all([
          usersPromise,
          permissionPromise,
        ]);

        /*
         * Permission API response
         */
        if (!permissionResponse.ok) {
          throw new Error("Failed to load cash permissions");
        }

        const permissionData = await permissionResponse.json();

        console.log("Users API:", usersResponse);
        console.log("Cash Permission API:", permissionData);

        const existingPermissions = Array.isArray(permissionData)
          ? permissionData
          : permissionData?.users || [];

        /*
         * Convert API data into UI rows
         */
        const rows = existingPermissions.map((item) => {
          const matchedUser = findUser(
            usersResponse?.users || users,
            item.user_id,
            item.employee_code,
          );

          return {
            id: item.id || null,

            user_id: item.user_id || matchedUser?.id || "",

            employee_code:
              item.employee_code || matchedUser?.employee_code || "",

            username:
              matchedUser?.username ||
              matchedUser?.user_name ||
              matchedUser?.name ||
              "",

            is_permission: Boolean(item.is_permission),
          };
        });

        setPermissionRows(rows);
      } catch (error) {
        console.error("Cash Module Permission loading error:", error);

        setPermissionRows([]);
      } finally {
        setLoadingPermissions(false);
      }
    };

    loadData();
  }, [companyId, token]);
  const getFilteredUsers = (currentRow) => {
    const selectedUserIds = permissionRows
      .filter((row) => row !== currentRow)
      .map((row) => String(row.user_id))
      .filter(Boolean);

    const search = employeeSearch.trim().toLowerCase();

    return users.filter((user) => {
      const userId = String(user.id ?? user.user_id ?? "");

      // Don't show already selected users
      if (selectedUserIds.includes(userId)) {
        return false;
      }

      const employeeCode = String(user.employee_code ?? "").toLowerCase();

      const username = String(
        user.username ?? user.user_name ?? user.name ?? "",
      ).toLowerCase();

      // Search by employee code OR name
      return (
        !search || employeeCode.includes(search) || username.includes(search)
      );
    });
  };
  /*
   * ============================================================
   * FIND USER
   * ============================================================
   */

  const findUser = (userList, userId, employeeCode) => {
    if (!Array.isArray(userList)) {
      return null;
    }

    return (
      userList.find(
        (user) =>
          String(user.id) === String(userId) ||
          String(user.user_id) === String(userId),
      ) ||
      userList.find(
        (user) => String(user.employee_code) === String(employeeCode),
      ) ||
      null
    );
  };

  /*
   * ============================================================
   * ADD ROW
   * ============================================================
   */

  const handleAddRow = () => {
    setPermissionRows((prev) => [
      ...prev,
      {
        id: null,
        user_id: "",
        employee_code: "",
        username: "",
        is_permission: false,
        isNew: true,
      },
    ]);
  };

  /*
   * ============================================================
   * REMOVE ROW
   * ============================================================
   */

  const handleRemoveRow = (index) => {
    setPermissionRows((prev) =>
      prev.filter((_, rowIndex) => rowIndex !== index),
    );
  };

  /*
   * ============================================================
   * SELECT USER
   * ============================================================
   */

  const handleUserChange = (index, userId) => {
    const selectedUser = users.find(
      (user) =>
        String(user.id) === String(userId) ||
        String(user.user_id) === String(userId),
    );

    if (!selectedUser) {
      return;
    }

    setPermissionRows((prev) =>
      prev.map((row, rowIndex) => {
        if (rowIndex !== index) {
          return row;
        }

        return {
          ...row,

          user_id: selectedUser.id ?? selectedUser.user_id ?? "",

          employee_code: selectedUser.employee_code || "",

          username:
            selectedUser.username ||
            selectedUser.user_name ||
            selectedUser.name ||
            "",

          is_permission: false,
        };
      }),
    );
  };

  /*
   * ============================================================
   * CHECKBOX
   * ============================================================
   */

  const handlePermissionChange = (index) => {
    setPermissionRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index
          ? {
              ...row,
              is_permission: !row.is_permission,
            }
          : row,
      ),
    );
  };

  /*
   * ============================================================
   * AVAILABLE USERS
   *
   * Don't allow the same user to be selected twice.
   * ============================================================
   */

  const getAvailableUsers = (currentRow) => {
    const selectedUserIds = permissionRows
      .filter((row) => row !== currentRow)
      .map((row) => String(row.user_id))
      .filter(Boolean);

    return users.filter((user) => {
      const id = String(user.id ?? user.user_id ?? "");

      return !selectedUserIds.includes(id);
    });
  };

  /*
   * ============================================================
   * SAVE
   * ============================================================
   */

  const handleSave = async () => {
    if (!companyId) {
      toastWarning("Company ID is missing.");
      return;
    }

    /*
     * Don't allow empty user rows
     */
    const invalidRow = permissionRows.some((row) => !row.user_id);

    if (invalidRow) {
      toastWarning("Please select an employee for every row.");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        users: permissionRows.map((row) => ({
          ...(row.id ? { id: Number(row.id) } : {}),
          company_id: Number(companyId),
          user_id: Number(row.user_id),
          employee_code: row.employee_code || "",
          is_permission: Boolean(row.is_permission),
        })),
      };

      console.log("Cash Module Permission Payload:", payload);

      const response = await fetch(
        `${API_URL}/cash-module-permission/${companyId}`,
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",

            ...(token && {
              Authorization: `Bearer ${token}`,
            }),
          },

          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);

        console.error("Cash permission save error:", errorData);

        throw new Error(
          errorData?.detail?.[0]?.msg ||
            errorData?.detail ||
            "Failed to save cash permissions",
        );
      }

      toastSuccess("Cash module permissions saved successfully.");

      /*
       * Refresh permission API after save
       */
      const refreshResponse = await fetch(
        `${API_URL}/cash-module-permission/${companyId}`,
        {
          method: "GET",

          headers: {
            Accept: "application/json",

            ...(token && {
              Authorization: `Bearer ${token}`,
            }),
          },
        },
      );

      if (refreshResponse.ok) {
        const refreshData = await refreshResponse.json();

        const refreshedPermissions = Array.isArray(refreshData)
          ? refreshData
          : refreshData?.users || [];

        const refreshedRows = refreshedPermissions.map((item) => {
          const matchedUser = findUser(users, item.user_id, item.employee_code);

          return {
            id: item.id || null,

            user_id: item.user_id || matchedUser?.id || "",

            employee_code:
              item.employee_code || matchedUser?.employee_code || "",

            username:
              matchedUser?.username ||
              matchedUser?.user_name ||
              matchedUser?.name ||
              "",

            is_permission: Boolean(item.is_permission),
          };
        });

        setPermissionRows(refreshedRows);
      }
    } catch (error) {
      console.error("Cash Module Permission save error:", error);

      toastError(error.message || "Failed to save cash module permissions.");
    } finally {
      setSaving(false);
    }
  };
  /*
   * ============================================================
   * LOADING
   * ============================================================
   */

  const pageLoading = loadingPermissions || usersLoading;

  /*
   * ============================================================
   * UI
   * ============================================================
   */

  return (
    <div className="cashpermission-container">
      {/* ================= HEADER ================= */}

      <div className="cashpermission-header">
        <h2>Cash Module Permission</h2>

        <button
          type="button"
          className="cashpermission-add-btn"
          onClick={handleAddRow}
          disabled={pageLoading}
        >
          <FaPlus />
          Add
        </button>
      </div>

      {/* ================= TABLE ================= */}

      <div className="cashpermission-table-wrapper">
        {pageLoading ? (
          <div className="cashpermission-center-loader">
            <FaSpinner className="cashpermission-spinner" />
            <span>Loading...</span>
          </div>
        ) : (
          <table className="cashpermission-table">
            <thead>
              <tr>
                <th className="cashpermission-sr-column">Sr. No.</th>

                <th>Employee Code</th>

                <th>Username</th>

                <th className="cashpermission-permission-column">Permission</th>

                <th className="cashpermission-action-column">Remove</th>
              </tr>
            </thead>

            <tbody>
              {permissionRows.length > 0 ? (
                permissionRows.map((row, index) => {
                  const availableUsers = getAvailableUsers(row);

                  return (
                    <tr key={row.id || `${row.user_id}-${index}`}>
                      {/* SR NO */}

                      <td className="cashpermission-center">{index + 1}</td>

                      {/* EMPLOYEE CODE */}

                      <td>
                        {row.isNew ? (
                          <div className="cashpermission-search-dropdown">
                            <button
                              type="button"
                              className="cashpermission-dropdown-btn"
                              onClick={() => {
                                setOpenEmployeeDropdown(
                                  openEmployeeDropdown === index ? null : index,
                                );

                                setEmployeeSearch("");
                              }}
                            >
                              {row.user_id
                                ? `${row.employee_code || "-"} - ${
                                    row.username || "-"
                                  }`
                                : "Select Employee"}

                              <span className="cashpermission-dropdown-arrow">
                                ▾
                              </span>
                            </button>

                            {openEmployeeDropdown === index && (
                              <div className="cashpermission-dropdown-menu">
                                {/* SEARCH */}

                                <div className="cashpermission-search-box">
                                  <input
                                    type="text"
                                    placeholder="Search by code or name..."
                                    value={employeeSearch}
                                    onChange={(e) =>
                                      setEmployeeSearch(e.target.value)
                                    }
                                    autoFocus
                                  />
                                </div>

                                {/* USERS */}

                                <div className="cashpermission-user-list">
                                  {getFilteredUsers(row).length > 0 ? (
                                    getFilteredUsers(row).map((user) => {
                                      const userId = user.id ?? user.user_id;

                                      const employeeCode =
                                        user.employee_code || "-";

                                      const username =
                                        user.username ||
                                        user.user_name ||
                                        user.name ||
                                        "-";

                                      return (
                                        <button
                                          type="button"
                                          key={userId}
                                          className="cashpermission-user-option"
                                          onClick={() => {
                                            handleUserChange(index, userId);

                                            setOpenEmployeeDropdown(null);
                                            setEmployeeSearch("");
                                          }}
                                        >
                                          <span className="employee-code">
                                            {employeeCode}
                                          </span>

                                          <span className="employee-name">
                                            {username}
                                          </span>
                                        </button>
                                      );
                                    })
                                  ) : (
                                    <div className="cashpermission-no-users">
                                      No employee found
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          row.employee_code || "-"
                        )}
                      </td>

                      {/* USERNAME */}

                      <td>{row.username || "-"}</td>

                      {/* CHECKBOX */}

                      <td className="cashpermission-center">
                        <label className="cashpermission-checkbox">
                          <input
                            type="checkbox"
                            checked={Boolean(row.is_permission)}
                            onChange={() => handlePermissionChange(index)}
                          />

                          <span className="cashpermission-checkmark" />
                        </label>
                      </td>

                      {/* REMOVE */}

                      <td className="cashpermission-center">
                        <button
                          type="button"
                          className="cashpermission-remove-btn"
                          onClick={() => handleRemoveRow(index)}
                          title="Remove"
                        >
                          <FaMinus />
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="5" className="cashpermission-no-data">
                    No cash permission configured.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* ================= BOTTOM SAVE ================= */}

      <div className="cashpermission-actions">
        <button
          type="button"
          className="cashpermission-save-btn"
          onClick={handleSave}
          disabled={saving || pageLoading}
        >
          {saving ? (
            <>
              <FaSpinner className="cashpermission-spinner" />
              Saving...
            </>
          ) : (
            "Save"
          )}
        </button>
      </div>
    </div>
  );
};

export default CashModulePermission;
